import { z } from 'zod';

/**
 * The one and only Google Workspace domain allowed to sign in (ADR-13).
 * `@kmutt.ac.th` is deliberately NOT accepted. This is a literal, not a list, so a second domain
 * cannot be added through configuration alone.
 */
export const ALLOWED_EMAIL_DOMAIN = 'mail.kmutt.ac.th';
export type AllowedEmailDomain = typeof ALLOWED_EMAIL_DOMAIN;

/** Student ID as typed at onboarding. Same pattern as `person.student_id` in M001. */
export const STUDENT_ID_PATTERN = /^[0-9]{2}[0-9A-Za-z-]{4,14}$/;

/**
 * PDPA notice shown next to the emergency-phone field (ADR-14). The Thai text is canonical and must
 * be rendered verbatim; the version is stored with the student's acknowledgement.
 */
export const EMERGENCY_PHONE_NOTICE = {
  version: 'pdpa-emergency-phone/1',
  th: 'เบอร์โทรศัพท์จะถูกนำไปใช้เพื่อการติดต่อประสานงานเร่งด่วนเท่านั้น โดยมีเฉพาะผู้ดูแลระบบที่เข้าถึงได้ และข้อมูลจะถูกลบทำลายออกจากระบบโดยอัตโนมัติเมื่อนักศึกษาผ่านการปลดภาระหนี้ (Clearance) สำเร็จ',
  en: 'Your phone number is used only for urgent coordination. Only system administrators can access it, and it is automatically and permanently deleted from the system once you complete Clearance.',
} as const;

/**
 * Normalizes a phone number to E.164. Thai local formats (0XXXXXXXX / 0XXXXXXXXX, with optional
 * spaces, dashes or dots) become +66…; numbers already in E.164 are kept. Returns null if invalid.
 */
export function normalizeEmergencyPhone(raw: string): string | null {
  const compact = raw.trim().replace(/[\s\-.()]/g, '');
  let e164: string;
  if (/^0[1-9][0-9]{7,8}$/.test(compact)) e164 = `+66${compact.slice(1)}`;
  else if (/^\+[1-9][0-9]{7,14}$/.test(compact)) e164 = compact;
  else return null;
  if (e164.startsWith('+66') && !/^\+66[1-9][0-9]{7,8}$/.test(e164)) return null;
  return e164;
}

const nameTh = z.string().trim().min(1).max(200).regex(/[฀-๿]/, 'must contain Thai script');
const nameEn = z.string().trim().min(1).max(200).regex(/^[A-Za-z][A-Za-z .'-]*$/, 'Latin letters only');

/** Input of the `identity.completeOnboarding` command (student self-onboarding, first sign-in only). */
export const StudentOnboardingInput = z.object({
  studentId: z.string().trim().regex(STUDENT_ID_PATTERN),
  nameTh,
  nameEn,
  emergencyPhone: z.string().transform((raw, ctx) => {
    const v = normalizeEmergencyPhone(raw);
    if (v === null) {
      ctx.addIssue({ code: 'custom', message: 'invalid phone number' });
      return z.NEVER;
    }
    return v;
  }),
  noticeVersion: z.literal(EMERGENCY_PHONE_NOTICE.version),
  noticeAccepted: z.literal(true),
});
export type StudentOnboardingInput = z.output<typeof StudentOnboardingInput>;

/** Field names that must never appear in a non-admin DTO (checked by the DTO allowlist test in 2.5). */
export const ADMIN_ONLY_FIELDS = ['emergencyPhone', 'emergency_phone', 'phoneE164', 'phone_e164'] as const;
